/**
 * Handles of a dynamic block reference: one per editable property.
 */

import type { IHandle } from "./handles/IHandle";
import type { Point3D } from "./types";
import type { GridHandlesContext } from "./gridHandles";
import { DynFlipHandle } from "./handles/DynFlipHandle";
import { DynListHandle } from "./handles/DynListHandle";
import { DynAngleHandle } from "./handles/DynAngleHandle";
import { DynPointHandle } from "./handles/DynPointHandle";
import {
  isDynamicBlockRef,
  listDynBlockProperties,
  groupDynBlockProperties,
  getDynBlockPointWorldPosition,
} from "../blocks/dynamic";
import { getEntityHandle } from "../core/oda";

/** Screen distance between the bounding box and the dynamic block value handles. */
const DYN_HANDLE_OFFSET_PIXELS = 34;

/** Vertical screen spacing between stacked dynamic block value handles. */
const DYN_HANDLE_SPACING_PIXELS = 34;

/**
 * One handle per editable dynamic block property.
 *
 * Point properties sit at their own world position (that is the whole point of
 * dragging them). The others have no geometry of their own and are stacked at a
 * fixed pixel distance from the bounding box: flips and value lists along the right
 * edge, rotations along the left one so that edge does not become unreadable.
 *
 * Empty on plain blocks and on engines older than DrawingWeb 27.7.
 */
export function buildDynamicBlockHandles(ctx: GridHandlesContext): IHandle[] {
  const { viewer, history, module, entityIds, keyPoints } = ctx;
  if (entityIds.length !== 1) return [];

  const entityId = entityIds[0];
  if (!isDynamicBlockRef(module, entityId)) return [];

  const handles: IHandle[] = [];
  const entityHandle = getEntityHandle(entityId);
  const { points, flips, lists, angles } = groupDynBlockProperties(
    listDynBlockProperties(module, entityId),
  );

  for (const point of points) {
    const worldPosition = getDynBlockPointWorldPosition(
      module,
      entityId,
      point.nameX,
      point.nameY,
    );
    if (!worldPosition) continue;
    handles.push(
      new DynPointHandle(
        viewer,
        history,
        entityHandle,
        point.nameX,
        point.nameY,
        worldPosition,
      ),
    );
  }

  // Stack the value-based handles vertically, outside the right edge.
  const spacing = viewer.screenDistanceToWorld(DYN_HANDLE_SPACING_PIXELS);
  const offset = viewer.screenDistanceToWorld(DYN_HANDLE_OFFSET_PIXELS);
  let slot = 0;
  const nextPosition = (): Point3D => ({
    x: keyPoints.rightMiddle.x + offset,
    y: keyPoints.rightMiddle.y - slot++ * spacing,
    z: 0,
  });

  for (const flip of flips) {
    handles.push(
      new DynFlipHandle(
        viewer,
        history,
        entityHandle,
        flip.name,
        flip.allowedValues,
        flip.value,
        nextPosition(),
      ),
    );
  }

  for (const list of lists) {
    handles.push(
      new DynListHandle(
        viewer,
        history,
        entityHandle,
        list.name,
        list.allowedValues,
        list.value,
        nextPosition(),
      ),
    );
  }

  // Rotations go on the LEFT edge: the right one is already three grips deep, and
  // stacking a fourth column there would bury the drawing itself.
  let leftSlot = 0;
  for (const angle of angles) {
    handles.push(
      new DynAngleHandle(
        viewer,
        history,
        entityHandle,
        angle.name,
        [],
        angle.value,
        {
          x: keyPoints.leftMiddle.x - offset,
          y: keyPoints.leftMiddle.y - leftSlot++ * spacing,
          z: 0,
        },
      ),
    );
  }

  return handles;
}
